import type {
    RpaBuilderWebsiteConfig,
    StoredRpaBuilderWebsiteConfig,
} from './backend_config.types';
import type { JsonValue } from './common.types';
import {
    IpxRbsWorkflowClass,
    type IpxRbsWorkflowElementBinding,
    type IpxRbsWorkflowWebsiteScope,
    type StoredIpxRbsWorkflowElementBindingList,
} from './rbs-ips-workflow.types';
import type {
    IpxDomRpaWorkflow,
    IpxWebResourceModelDocument,
    StoredIpxDomRpaWorkflow,
    StoredIpxWebResourceModelDocument,
} from './samba-rpa-workflow.types';

export interface RbsWorkflow {
    readonly workflow: IpxDomRpaWorkflow;
    readonly input_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly output_bindings: readonly IpxRbsWorkflowElementBinding[];
    readonly class_metadata: Readonly<Record<string, JsonValue>>;
}

export type RbsWorkflowMap = {
    readonly [WorkflowClass in IpxRbsWorkflowClass]: readonly RbsWorkflow[];
};

export interface RbsWebsite {
    readonly rbs_config: RpaBuilderWebsiteConfig;
    readonly web_resource_model: IpxWebResourceModelDocument | null;
    readonly login_workflow: IpxDomRpaWorkflow | null;
    readonly workflows: RbsWorkflowMap;
}

export interface RpaBuilderWebsiteConfigurationProjection {
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
    readonly rbs_website: RbsWebsite | null;
    readonly web_resource_model_digest: string | null;
    readonly ipx_credential_definition_uuid: string | null;
}

export interface RpaBuilderLoadWebsiteConfigurationRequest {
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
}

export interface RpaBuilderLoadWebsiteConfigurationResponse {
    readonly configuration: RpaBuilderWebsiteConfigurationProjection;
}

export type RbsWorkflowMapPatch = {
    readonly [WorkflowClass in IpxRbsWorkflowClass]?: readonly RbsWorkflow[];
};

export interface RbsWebsitePatch {
    readonly rbs_config?: RpaBuilderWebsiteConfig;
    readonly web_resource_model?: IpxWebResourceModelDocument | null;
    readonly login_workflow?: IpxDomRpaWorkflow | null;
    readonly workflows?: RbsWorkflowMapPatch;
}

export interface RpaBuilderSaveWebsiteConfigurationRequest {
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
    readonly patch: RbsWebsitePatch;
    readonly ipx_credential_definition_uuid?: string | null;
}

export interface RpaBuilderSaveWebsiteConfigurationResponse {
    readonly configuration: RpaBuilderWebsiteConfigurationProjection;
}

export interface RpaBuilderResetWebsiteConfigurationRequest {
    readonly website_scope: IpxRbsWorkflowWebsiteScope;
}

export interface RpaBuilderResetWebsiteConfigurationResponse {
    readonly configuration: RpaBuilderWebsiteConfigurationProjection;
}

export const RbsWebsiteStorageVersions = {
    V1: 1,
} as const;

export type RbsWebsiteStorageVersion =
    (typeof RbsWebsiteStorageVersions)[keyof typeof RbsWebsiteStorageVersions];

export interface RbsWorkflowStorageV1 {
    readonly workflow: StoredIpxDomRpaWorkflow;
    readonly input_bindings: StoredIpxRbsWorkflowElementBindingList;
    readonly output_bindings: StoredIpxRbsWorkflowElementBindingList;
    readonly class_metadata: Readonly<Record<string, JsonValue>>;
}

export type RbsWorkflowMapStorageV1 = {
    readonly [WorkflowClass in IpxRbsWorkflowClass]: readonly RbsWorkflowStorageV1[];
};

export interface RbsWebsiteStorageV1 {
    readonly rbs_website_version: typeof RbsWebsiteStorageVersions.V1;
    readonly rbs_config: StoredRpaBuilderWebsiteConfig;
    readonly web_resource_model: StoredIpxWebResourceModelDocument | null;
    readonly login_workflow: StoredIpxDomRpaWorkflow | null;
    readonly workflows: RbsWorkflowMapStorageV1;
}
